import dayjs from 'dayjs'
import { groupSubtotal } from '@/utils/dd/entryHelpers'
import { LIQUID_ASSET_GROUPS, OTHER_ASSET_GROUPS } from '@/utils/dd/constants'

// --------------------------------------------------------------------------
// DD Snapshot — precision policy + builder
//
// A snapshot is a point-in-time capture of the full DD state (raw inputs +
// every frontend-computed value). It is rewritten on every save and "frozen"
// (stamped server-side) when a deal enters the Underwriting Queue.
//
// Precision policy (extensible): currency → 2dp, rates/percentages → 4dp.
// Rounding happens here, at build time, so stored data + reports stay stable
// and free of JS float noise (e.g. 18.67850000001).
// --------------------------------------------------------------------------

export const PRECISION = { currency: 2, rate: 4 }

export function roundForSnapshot(value, kind = 'currency') {
  if (value == null || value === '') return value
  const num = Number(value)
  if (Number.isNaN(num)) return value
  const dp = PRECISION[kind] ?? PRECISION.currency
  const factor = 10 ** dp
  return Math.round(num * factor) / factor
}

// Top-level calc keys that are rates/percentages; everything else is currency.
const RATE_KEYS = new Set(['atvRate', 'atvAllowableDropRate'])

function roundCalcs(calcs) {
  const out = {}
  for (const [key, val] of Object.entries(calcs || {})) {
    out[key] =
      typeof val === 'number'
        ? roundForSnapshot(val, RATE_KEYS.has(key) ? 'rate' : 'currency')
        : val
  }
  return out
}

// Per-property real-estate calcs are all dollar amounts (currency).
function roundRealEstateCalcs(list) {
  return (list || []).map((property) => {
    const out = { ...property }
    for (const [key, val] of Object.entries(property)) {
      if (typeof val === 'number' && key !== 'id') {
        out[key] = roundForSnapshot(val, 'currency')
      }
    }
    return out
  })
}

function buildGroupSubtotals(entries, groups) {
  return groups.map((grp) => ({
    groupKey: grp.key,
    label: grp.label,
    subtotal: roundForSnapshot(groupSubtotal(entries, grp.key), 'currency')
  }))
}

const clone = (obj) => JSON.parse(JSON.stringify(obj ?? null))

// Pull every derived value off the store's aggregated `totals` object.
function extractCalcs(totals) {
  const keys = [
    'realEstate',
    'liquidAssets',
    'otherAssets',
    'netTotalAssets',
    'grossTotalAssets',
    'costsAndClaims',
    'excludedRealEstate',
    'excludedLiquidAssets',
    'excludedOtherAssets',
    'residualForDist',
    'remainingShareToDist',
    'atvTotalAssignments',
    'atvRate',
    'atvAllowableDrop',
    'atvAllowableDropRate',
    'atvAllowableDropDiff',
    'securedDebt',
    'unsecuredDebt',
    'heirShare',
    'heirNetInheritance'
  ]
  const calcs = {}
  keys.forEach((k) => {
    calcs[k] = totals[k]
  })
  return calcs
}

// Build the full snapshot object (camelCase). Callers decamelize before sending.
export function buildSnapshotData({ ddDeal, ddEstate, ddClient, totals, related }) {
  const ccc = ddEstate.costsAndClaimsCalcs
  const edc = ddEstate.estateDistributionCalcs
  const cgt = ddClient.grandTotals

  // Exclude server-managed fields that must not recurse into the snapshot inputs.
  const { snapshot: _snap, snapshotAtvRate: _snapAtv, ...ddDealModelFields } = ddDeal.model
  const ddDealInput = clone(ddDealModelFields)

  return {
    version: 1,
    captured_at: dayjs().toISOString(),
    inputs: {
      ddDeal: ddDealInput,
      ddEstate: clone(ddEstate.model),
      ddClient: clone(ddClient.model),
      otherClients: clone(ddEstate.otherClients),
      members: clone(ddEstate.members),
      realEstate: clone(ddEstate.realEstate),
      entities: clone(ddEstate.entities),
      bequests: clone(ddEstate.bequests),
      estateTransactions: clone(ddEstate.estateTransactions),
      clientDeals: clone(related.clientDeals),
      competitorDeals: clone(related.competitorDeals),
      previouslyFundedDeals: clone(related.previouslyFundedDeals),
      metaData: clone(related.metaData)
    },
    calcs: roundCalcs(extractCalcs(totals)),
    realEstateCalcs: roundRealEstateCalcs(clone(ddEstate.realEstateCalcs)),
    groupSubtotals: {
      liquidAssets: buildGroupSubtotals(ddEstate.model.liquidAssets, LIQUID_ASSET_GROUPS),
      otherAssets: buildGroupSubtotals(ddEstate.model.otherAssets, OTHER_ASSET_GROUPS)
    },
    detailedCalcs: roundCalcs({
      // Costs & Claims computed lines
      statutoryFeeBase:    ccc.statutoryFeeBase,
      statutoryFee:        ccc.statutoryFee,
      attorneyAdminFees:   ccc.attorneyAdminFees,
      taxableAmount:       ccc.taxableAmount,
      taxExemptionAmount:  ccc.taxExemptionAmount,
      fedEstateTaxes:      ccc.fedEstateTaxes,
      stateInheritanceTax: ccc.stateInheritanceTax,
      // Estate distribution steps
      grossAvailable:      edc.grossAvailable,
      prelimDistTotal:     edc.prelimDistTotal,
      specBeqTotal:        edc.specBeqTotal,
      sharedResidual:      edc.sharedResidual,
      // Heir distribution
      prelimDistClient:    cgt.prelimDistClient,
      specificBeqClient:   cgt.specificBeqClient,
    })
  }
}
